import React, { useState, useEffect } from 'react';
import P, { InferProps } from 'prop-types';
import { SxStyle } from '../../../types';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { useStringsContext } from '../../../contexts/strings';
import ListSubheader from '@mui/material/ListSubheader';
import { OptionsGroup, Option } from '../../../types';
import FormHelperText from '@mui/material/FormHelperText';

const styles: SxStyle = {
  formControl: {
    minWidth: 200,
  },
  header: (theme) => ({
    fontSize: '16px',
    color: theme.palette.menu.main,
    '&::first-letter': {
      textTransform: 'capitalize',
    },
  }),
};

function SelectField({
  field,
  setChange,
  setValidation,
  isReadOnly,
  fullWidth = false,
  size = 'medium',
  minWidth,
  hideValidationError,
}: InferProps<typeof SelectField.propTypes>) {
  const { strings } = useStringsContext();
  const [value, setValue] = useState<string | number | boolean>(field.value || '');
  const [open, setOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDirty, setIsdirty] = useState(false);

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e: SelectChangeEvent<string | number | boolean>) => {
    const value = e.target.value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      setValue(value);

      if (setChange) {
        setChange(field.key, value, field.validation, field.isTeamDataValue);
      }

      setIsdirty(true);
    }
  };

  useEffect(() => {
    const validate = async (newValue: string | number | boolean) => {
      const yupSchema = field.validation;
      const parsedValue: number | string | boolean | null = newValue;

      if (!yupSchema || !setValidation) {
        return;
      }

      try {
        await yupSchema.validate(newValue);
        setErrorMessage('');
      } catch (error: any) {
        if (isDirty || !hideValidationError) {
          setErrorMessage(error.message);
        }
      } finally {
        if (setValidation) {
          setValidation(field.key, parsedValue, field.validation);
        }
      }
    };

    validate(value);
  }, [hideValidationError, field, isDirty, setValidation, value]);

  const renderValue = (value: string | number | boolean) => {
    let renderValue = null;

    // Use the value from field.optionsText (if exists) instead of field.options
    if (field.options && field.optionsText && value !== '') {
      const optionsIndex = field.options.indexOf(value);
      renderValue = field.optionsText[optionsIndex] || value;

      // or use the value from field.groupedOptions...optionsText (if exists)
    } else if (field.groupedOptions && value !== '') {
      const optionsGroup = field.groupedOptions.find((group) =>
        group.options.find((option) => option === value)
      )!;
      const optionsIndex = optionsGroup.options.indexOf(value);
      renderValue = (optionsGroup.optionsText && optionsGroup.optionsText[optionsIndex]) || value;
    } else {
      renderValue = value;
    }
    return renderValue;
  };

  const renderOption = (
    option: Option,
    optionsText: string[] | undefined | null,
    index: number
  ) => {
    const text = optionsText ? optionsText[index] : option;
    return (
      <MenuItem key={`${field.id}-${index}`} value={option as string}>
        {text === '' ? <em>{strings.messages.none}</em> : (text as string)}
      </MenuItem>
    );
  };

  const renderGroup = (optionsGroup: OptionsGroup) => {
    const optionsText = optionsGroup.optionsText;
    const options = optionsGroup.options.map((option, index) =>
      renderOption(option, optionsText, index)
    );
    return [
      <ListSubheader sx={styles.header} color="primary" key={optionsGroup.name}>
        {optionsGroup.name}
      </ListSubheader>,
      options,
    ];
  };

  return (
    <FormControl
      variant="outlined"
      sx={styles.formControl}
      style={minWidth ? { minWidth } : {}}
      fullWidth={fullWidth || false}
    >
      <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
        {field.title}
      </InputLabel>
      <Select
        size={size as 'small' | 'medium'}
        disabled={field.key === 'Not available' || !!isReadOnly}
        labelId={`${field.id}-label`}
        id={field.id}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        value={value}
        onChange={handleChange}
        renderValue={renderValue}
        color="primary"
        label={field.title}
        error={!!errorMessage}
      >
        {field.groupedOptions
          ? field.groupedOptions.map((group) => renderGroup(group))
          : field.options?.map((option: Option, index) =>
              renderOption(option, field.optionsText, index)
            )}
      </Select>
      <FormHelperText error>{errorMessage}</FormHelperText>
    </FormControl>
  );
}

SelectField.propTypes = {
  setChange: P.func,
  setValidation: P.func,
  isReadOnly: P.bool,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any,
    key: P.string,
    groupedOptions: P.arrayOf(
      P.shape({
        name: P.string.isRequired,
        options: P.arrayOf(P.oneOfType([P.string, P.number, P.bool]).isRequired).isRequired,
        optionsText: P.arrayOf(P.string.isRequired),
      }).isRequired
    ),
    options: P.arrayOf(P.oneOfType([P.string, P.number, P.bool]).isRequired),
    optionsText: P.arrayOf(P.string.isRequired),
    value: P.any.isRequired,
    isTeamDataValue: P.bool,
  }).isRequired,
  fullWidth: P.bool,
  size: P.oneOf(['small', 'medium']),
  minWidth: P.number,
  hideValidationError: P.bool,
};

export default React.memo(SelectField);
